// packages/ui/src/components/chat/ChatComposer.tsx
'use client';

import { useState } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import SendIcon from '@mui/icons-material/Send';
import type { TFunc } from '@tourtask/types/i18n';
// Outlined-only: MUI's TextField would bring its Select into chat.js (docs/embed-size-and-speed.md §4).
import { OutlinedTextField } from '@tourtask/ui/inputs/OutlinedTextField';

interface ChatComposerProps {
  /** Called with the trimmed text; the field empties at once. */
  onSend: (text: string) => void | Promise<unknown>;
  disabled?: boolean;
  isSending?: boolean;
  t: TFunc;
  placeholder?: string;
  autoFocus?: boolean;
  /** The field's text, when its owner keeps it (with `onValueChange`), as
   *  `useChatDraft` does: a message that did not go through comes back into the
   *  field, also one asked elsewhere on the page. Without it the composer keeps
   *  its own. */
  value?: string;
  onValueChange?: (text: string) => void;
}

/**
 * The shared composer — a multiline input + send button. Enter sends,
 * Shift+Enter inserts a newline. Pure: `onSend` + `t` in, no transport.
 */
export function ChatComposer({
  onSend,
  disabled,
  isSending,
  t,
  placeholder,
  autoFocus,
  value: controlledValue,
  onValueChange,
}: ChatComposerProps) {
  const [ownValue, setOwnValue] = useState('');
  const controlled = controlledValue !== undefined;
  const value = controlled ? controlledValue : ownValue;
  const setValue = (text: string) => (controlled ? onValueChange?.(text) : setOwnValue(text));
  const canSend = value.trim().length > 0 && !disabled && !isSending;

  const submit = () => {
    const text = value.trim();
    if (!text || disabled || isSending) return;
    void onSend(text);
    setValue('');
  };

  return (
    <Box
      sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, borderTop: 1, borderColor: 'divider' }}
    >
      <OutlinedTextField
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            submit();
          }
        }}
        placeholder={placeholder ?? t('Type_your_message')}
        multiline
        maxRows={4}
        size="small"
        fullWidth
        autoFocus={autoFocus}
        disabled={disabled}
        // The widget mounts inside arbitrary host pages whose themes style bare
        // `textarea` (extra padding bleeds in — very visible on the left). Pin the
        // field's own padding so only our tight, intended spacing shows.
        sx={{
          '& .MuiInputBase-root': { px: 1.25, py: 1 },
          '& .MuiInputBase-input': { p: 0 },
        }}
      />
      {/* No in-button spinner while the bot replies — the typing bubble already
          signals that; the button is simply disabled (canSend is false while
          isSending). */}
      <IconButton color="primary" aria-label={t('Send')} onClick={submit} disabled={!canSend}>
        <SendIcon />
      </IconButton>
    </Box>
  );
}
